/**
 * Container List Component
 *
 * Displays a list of containers running on a remote compute node.
 * Shows container status, type, resource allocation, and provides
 * controls for managing container lifecycle.
 */

import { useState, useCallback } from 'react';
import { getApiClient, isSuccess } from '../../api/client';

// =============================================================================
// Types
// =============================================================================

/**
 * Container status enum matching backend
 */
type ContainerStatus = 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR';

/**
 * Container type enum matching backend
 */
type ContainerType =
  | 'VLLM'
  | 'OLLAMA'
  | 'TTS_CHATTERBOX'
  | 'TTS_OTHER'
  | 'STT_WHISPER'
  | 'STT_OTHER'
  | 'IMAGE_SD'
  | 'IMAGE_OTHER';

/**
 * Container data structure
 */
export interface ContainerData {
  id: string;
  nodeId: string;
  type: ContainerType;
  image: string;
  status: ContainerStatus;
  config: Record<string, unknown>;
  resources: {
    gpuDevices?: string;
    memoryMb?: number;
    cpuCores?: number;
  };
  port: number | null;
  healthUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Props for ContainerList component
 */
export interface ContainerListProps {
  /** Node ID to list containers for */
  nodeId: string;
  /** List of containers */
  containers: ContainerData[];
  /** Whether the container list is loading */
  isLoading?: boolean;
  /** Callback when container status changes */
  onContainerChange?: () => void;
  /** Whether the user can manage containers */
  canManage?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<ContainerStatus, { bg: string; text: string; dot: string }> = {
  RUNNING: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  STOPPED: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
  STARTING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  STOPPING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  ERROR: { bg: 'bg-red-900/20', text: 'text-red-400', dot: 'bg-red-400' },
};

const CONTAINER_TYPE_LABELS: Record<ContainerType, string> = {
  VLLM: 'vLLM',
  OLLAMA: 'Ollama',
  TTS_CHATTERBOX: 'Chatterbox TTS',
  TTS_OTHER: 'TTS',
  STT_WHISPER: 'Whisper STT',
  STT_OTHER: 'STT',
  IMAGE_SD: 'Stable Diffusion',
  IMAGE_OTHER: 'Image Gen',
};

const CONTAINER_TYPE_ICONS: Record<ContainerType, React.ReactNode> = {
  VLLM: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  OLLAMA: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  TTS_CHATTERBOX: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  ),
  TTS_OTHER: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  ),
  STT_WHISPER: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
    </svg>
  ),
  STT_OTHER: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  IMAGE_SD: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  IMAGE_OTHER: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Format memory for display
 */
function formatMemory(mb?: number): string {
  if (mb === undefined || mb === null) return 'N/A';
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb} MB`;
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: ContainerStatus }) {
  const colors = STATUS_COLORS[status];

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {status}
    </div>
  );
}

/**
 * Container row component
 */
interface ContainerRowProps {
  container: ContainerData;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: 'start' | 'stop') => void;
  isLoading: boolean;
  canManage: boolean;
}

function ContainerRow({
  container,
  onDelete,
  onStatusChange,
  isLoading,
  canManage,
}: ContainerRowProps) {
  const isRunning = container.status === 'RUNNING';
  const isTransitioning = container.status === 'STARTING' || container.status === 'STOPPING';

  return (
    <tr className="border-b border-discord-light hover:bg-discord-light/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="text-gray-400">
            {CONTAINER_TYPE_ICONS[container.type]}
          </div>
          <div>
            <div className="font-medium text-white">
              {CONTAINER_TYPE_LABELS[container.type]}
            </div>
            <div className="text-xs text-gray-500 font-mono truncate max-w-[200px]" title={container.image}>
              {container.image}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={container.status} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        <div className="space-y-1">
          {container.resources.gpuDevices && (
            <div className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <span>GPU: {container.resources.gpuDevices}</span>
            </div>
          )}
          {container.resources.memoryMb && (
            <div className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Mem: {formatMemory(container.resources.memoryMb)}</span>
            </div>
          )}
          {container.resources.cpuCores && (
            <div className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <span>CPU: {container.resources.cpuCores} cores</span>
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {container.port ? (
          <span className="font-mono text-blue-400">:{container.port}</span>
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {formatTimestamp(container.createdAt)}
      </td>
      {canManage && (
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {isRunning && (
              <button
                onClick={() => onStatusChange(container.id, 'stop')}
                disabled={isLoading || isTransitioning}
                className="rounded-md bg-yellow-600/20 px-2.5 py-1 text-xs font-medium text-yellow-400 hover:bg-yellow-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Stop container"
              >
                Stop
              </button>
            )}
            {container.status === 'STOPPED' && (
              <button
                onClick={() => onStatusChange(container.id, 'start')}
                disabled={isLoading || isTransitioning}
                className="rounded-md bg-green-600/20 px-2.5 py-1 text-xs font-medium text-green-400 hover:bg-green-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Start container"
              >
                Start
              </button>
            )}
            <button
              onClick={() => onDelete(container.id)}
              disabled={isLoading || isRunning}
              className="rounded-md bg-red-600/20 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={isRunning ? 'Stop container first' : 'Delete container'}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

/**
 * Empty state component
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">No containers</h3>
      <p className="mt-1 text-sm text-gray-400">
        No containers are running on this node
      </p>
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      <table className="w-full">
        <thead className="bg-discord-darker">
          <tr>
            <th className="px-4 py-3 text-left"><div className="h-4 w-16 bg-discord-light rounded" /></th>
            <th className="px-4 py-3 text-left"><div className="h-4 w-16 bg-discord-light rounded" /></th>
            <th className="px-4 py-3 text-left"><div className="h-4 w-20 bg-discord-light rounded" /></th>
            <th className="px-4 py-3 text-left"><div className="h-4 w-12 bg-discord-light rounded" /></th>
            <th className="px-4 py-3 text-left"><div className="h-4 w-16 bg-discord-light rounded" /></th>
            <th className="px-4 py-3 text-left"><div className="h-4 w-16 bg-discord-light rounded" /></th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3].map((i) => (
            <tr key={i} className="border-b border-discord-light">
              <td className="px-4 py-3"><div className="h-10 w-40 bg-discord-light rounded" /></td>
              <td className="px-4 py-3"><div className="h-6 w-20 bg-discord-light rounded-full" /></td>
              <td className="px-4 py-3"><div className="h-6 w-24 bg-discord-light rounded" /></td>
              <td className="px-4 py-3"><div className="h-6 w-12 bg-discord-light rounded" /></td>
              <td className="px-4 py-3"><div className="h-6 w-16 bg-discord-light rounded" /></td>
              <td className="px-4 py-3"><div className="h-6 w-20 bg-discord-light rounded" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Container list component
 *
 * Displays containers for a remote node with status, resources, and actions.
 */
export function ContainerList({
  nodeId,
  containers,
  isLoading = false,
  onContainerChange,
  canManage = false,
}: ContainerListProps) {
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle container status change
   */
  const handleStatusChange = useCallback(
    async (containerId: string, action: 'start' | 'stop') => {
      setActionLoading(true);
      setError(null);

      const apiClient = getApiClient();
      const newStatus = action === 'start' ? 'STARTING' : 'STOPPING';

      try {
        const result = await apiClient.put<{ id: string }>(
          `/nodes/${nodeId}/containers/${containerId}/status`,
          { status: newStatus }
        );

        if (isSuccess(result)) {
          onContainerChange?.();
        } else {
          setError(result.error.message || `Failed to ${action} container`);
        }
      } catch (err) {
        setError(`Failed to ${action} container. Please try again.`);
      } finally {
        setActionLoading(false);
      }
    },
    [nodeId, onContainerChange]
  );

  /**
   * Handle container delete
   */
  const handleDelete = useCallback(
    async (containerId: string) => {
      if (!confirm('Are you sure you want to delete this container?')) {
        return;
      }

      setActionLoading(true);
      setError(null);

      const apiClient = getApiClient();

      try {
        const result = await apiClient.delete<{ id: string; deleted: boolean }>(
          `/nodes/${nodeId}/containers/${containerId}`
        );

        if (isSuccess(result)) {
          onContainerChange?.();
        } else {
          setError(result.error.message || 'Failed to delete container');
        }
      } catch (err) {
        setError('Failed to delete container. Please try again.');
      } finally {
        setActionLoading(false);
      }
    },
    [nodeId, onContainerChange]
  );

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (containers.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-900/50 border border-red-500 p-3">
          <div className="flex items-start">
            <svg
              className="mr-2 h-4 w-4 flex-shrink-0 text-red-400 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1 text-sm text-red-200">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-2 text-xs font-medium text-red-400 hover:text-red-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Container Table */}
      <div className="rounded-lg border border-discord-light overflow-hidden">
        <table className="w-full">
          <thead className="bg-discord-darker">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                Resources
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                Port
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                Created
              </th>
              {canManage && (
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-discord-dark">
            {containers.map((container) => (
              <ContainerRow
                key={container.id}
                container={container}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
                isLoading={actionLoading}
                canManage={canManage}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="text-sm text-gray-400">
        {containers.length} container{containers.length === 1 ? '' : 's'} ·{' '}
        {containers.filter((c) => c.status === 'RUNNING').length} running
      </div>
    </div>
  );
}

export default ContainerList;
